import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar } from 'lucide-react';
import lifeStageContent from '../data/lifeStageContent';

const LifeStagesCard = ({ birthDate, currentDate }) => {
  const [selectedStageIndex, setSelectedStageIndex] = useState(null);
  useEffect(() => {
    setSelectedStageIndex(null);
  }, [birthDate]);

  const calculateLifeStages = (value) => {
    if (!value || typeof value !== 'string') return [];
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return [];
    const [, yearStr, monthStr, dayStr] = match;
    const birthYear = Number(yearStr);
    const birthMonth = Number(monthStr);
    const birthDay = Number(dayStr);
    if ([birthYear, birthMonth, birthDay].some((n) => Number.isNaN(n))) return [];

    const reducedYear = reduceNum(birthYear);
    const reducedMonth = reduceNum(birthMonth);
    const reducedDay = reduceNum(birthDay);

    const stageOne = reduceNum(reducedMonth + reducedDay);
    const stageTwo = reduceNum(reducedDay + reducedYear);
    const stageThree = reduceNum(stageOne + stageTwo);
    const stageFour = reduceNum(birthMonth + birthYear);
    const stageNumbers = [
      stageOne,
      stageTwo,
      stageThree,
      stageFour,
      stageThree,
      stageTwo,
      stageOne
    ];

    const lifePath = reduceNum(birthDay + birthMonth + birthYear);
    const firstStageLength = 36 - lifePath;
    const ageBoundaries = [];
    let startAge = 0;
    let endAge = firstStageLength;
    ageBoundaries.push({ startAge, endAge });

    for (let i = 0; i < 5; i += 1) {
      startAge = endAge;
      endAge = endAge + 9;
      ageBoundaries.push({ startAge, endAge });
    }

    ageBoundaries.push({ startAge: endAge, endAge: Infinity });

    const stageMeta = [
      { name: 'Primera Etapa', description: 'Período de formación y aprendizaje básico' },
      { name: 'Segunda Etapa', description: 'Período de independencia y desarrollo personal' },
      { name: 'Tercera Etapa', description: 'Período de expansión y logros materiales' },
      { name: 'Cuarta Etapa', description: 'Período de estabilización y sabiduría' },
      { name: 'Quinta Etapa', description: 'Período de transformación y cambio' },
      { name: 'Sexta Etapa', description: 'Período de servicio y responsabilidad' },
      { name: 'Séptima Etapa', description: 'Período de reflexión y trascendencia' }
    ];

    const dateFromAge = (age) => new Date(Date.UTC(birthYear + age, birthMonth - 1, birthDay, 12, 0, 0));
    const formatAgeRange = (start, end) => (Number.isFinite(end) ? `${start}-${end}` : `${start}+`);
    const formatYearRange = (start, end) => {
      const startYear = birthYear + start;
      if (!Number.isFinite(end)) return `${startYear} en adelante`;
      return `${startYear}-${birthYear + end}`;
    };

    return ageBoundaries.map((range, index) => {
      const meta = stageMeta[index];
      return {
        ...meta,
        number: stageNumbers[index],
        ageRange: formatAgeRange(range.startAge, range.endAge),
        years: formatYearRange(range.startAge, range.endAge),
        description: meta.description,
        startDate: dateFromAge(range.startAge),
        endDate: Number.isFinite(range.endAge) ? dateFromAge(range.endAge) : null
      };
    });
  };

  // Función para reducir números (respetando 11 y 22)
  const reduceNum = (n) => {
    let x = Math.abs(n);
    while (x >= 10 && x !== 11 && x !== 22) {
      x = x.toString().split("").reduce((a, b) => a + Number(b), 0);
    }
    return x;
  };

  // Función alternativa que podría ser la correcta
  const reduceNumAlt = (n) => {
    let x = Math.abs(n);
    while (x > 9) {
      x = x.toString().split("").reduce((a, b) => a + Number(b), 0);
    }
    return x;
  };

  const getCurrentStage = (stages, dateValue) => {
    if (!stages.length) return { stage: null, index: 0 };
    const current = dateValue instanceof Date ? dateValue : new Date(dateValue);
    const currentTime = current.getTime();

    for (let i = 0; i < stages.length; i += 1) {
      const stage = stages[i];
      const startTime = stage.startDate?.getTime?.() ?? -Infinity;
      const endTime = stage.endDate ? stage.endDate.getTime() : Infinity;
      if (currentTime >= startTime && currentTime < endTime) {
        return { stage, index: i };
      }
    }

    return { stage: stages[stages.length - 1], index: stages.length - 1 };
  };

  const stages = calculateLifeStages(birthDate);
  const resolvedCurrentDate = currentDate || new Date();
  const currentStageInfo = stages.length ? getCurrentStage(stages, resolvedCurrentDate) : null;

  if (!stages.length || !currentStageInfo?.stage) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="rounded-2xl shadow-xl border overflow-hidden"
        style={{ borderColor: '#d4af37' }}
      >
        <div className="p-6 text-center text-sm text-gray-600">
          Necesitamos una fecha de nacimiento válida para calcular tus Etapas de Vida.
        </div>
      </motion.div>
    );
  }

  const displayStageInfo = selectedStageIndex !== null 
    ? { ...currentStageInfo, stage: stages[selectedStageIndex], index: selectedStageIndex }
    : currentStageInfo;

  const defaultStageSummaries = {
      1: "Independencia y liderazgo",
      2: "Colaboración y sensibilidad", 
      3: "Expresión y creatividad",
      4: "Orden y estabilidad",
      5: "Cambio y libertad",
      6: "Servicio y responsabilidad",
      7: "Sabiduría y reflexión",
      8: "Poder y logros",
      9: "Cierre y compasión",
      11: "Inspiración y visión",
      22: "Construcción y progreso"
    };

  const getStageSummary = (number) => {
    return lifeStageContent[number]?.summary || defaultStageSummaries[number] || "Energía única";
  };

  const hashString = (value) => {
    if (!value) return 0;
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
      hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
    }
    return hash;
  };

  const getConsistentDeepMessage = (stageNumber, birth) => {
    const content = lifeStageContent[stageNumber];
    if (!content?.deepMessages?.length) return null;
    const key = `${birth || ''}-${stageNumber}`;
    const hash = hashString(key);
    const index = hash % content.deepMessages.length;
    return content.deepMessages[index];
  };

  const activeStageNumber = displayStageInfo.stage.number;
  const activeStageContent = lifeStageContent[activeStageNumber];
  const stageSummary = getStageSummary(activeStageNumber);
  const deepMessage = getConsistentDeepMessage(activeStageNumber, birthDate);
  const hasActions = Boolean(
    activeStageContent?.actions?.do?.length || activeStageContent?.actions?.avoid?.length
  );

  // Función para obtener ordinales (1ra, 2da, 3ra, etc.)
  const getOrdinal = (index) => {
    const ordinals = ["1ra", "2da", "3ra", "4ta", "5ta", "6ta", "7ma"];
    return ordinals[index] || `${index + 1}`;
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="rounded-2xl shadow-xl border overflow-hidden"
      style={{ borderColor: "#d4af37" }}
    >
      {/* Header */}
      <div className="p-4 md:p-5 text-white" style={{ background: "var(--color-morado)" }}>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-white/15">
            <Calendar size={22} />
          </div>
          <div>
            <h4 className="text-lg md:text-xl font-semibold">Etapa de Vida Actual</h4>
            <p className="text-sm opacity-90">Tu ciclo numerológico de vida</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 md:p-5 bg-white">
        {/* Gráfica Mejorada */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-gray-600">Lecciones que te toca pasar en tu vida</span>
            <span className="text-xs text-gray-500">Etapa actual resaltada</span>
          </div>
          
          {/* Timeline Horizontal */}
          <div className="relative">
            <div className="flex items-center justify-between">
              {stages.map((stage, index) => (
                <div key={index} className="flex flex-col items-center flex-1">
                  <button
                    onClick={() => setSelectedStageIndex(selectedStageIndex === index ? null : index)}
                    className={`
                      w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-xs md:text-sm font-bold border-2 transition-all mb-2 cursor-pointer hover:scale-105
                      ${index === displayStageInfo.index 
                        ? 'bg-[#d4af37] text-white border-[#d4af37] scale-110 shadow-lg' 
                        : 'bg-white text-gray-600 border-gray-300 hover:border-[#d4af37] hover:text-[#d4af37]'
                      }
                    `}
                    title={`Ver ${stage.name}`}
                  >
                    {getOrdinal(index)}
                  </button>
                  <div className="text-xs text-gray-500 text-center mb-1">
                    {stage.ageRange}
                  </div>
                  <div className="w-full h-0.5 bg-gray-200"></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Información Actual Mejorada */}
        <div className="bg-morado/10 rounded-xl p-4 border border-morado/20">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-morado bg-white border-2 border-[#d4af37] text-lg">
              {displayStageInfo.stage.number}
            </div>
            <div>
              <h5 className="font-semibold text-morado text-lg">
                {displayStageInfo.stage.name}
              </h5>
              <p className="text-sm text-gray-600">
                {displayStageInfo.stage.ageRange} años • {displayStageInfo.stage.years}
              </p>
            </div>
          </div>
          
          <div className="space-y-2">
            <p className="text-sm text-gray-700">
              <span className="font-medium">¿Qué significa?</span> {displayStageInfo.stage.description}
            </p>
            <p className="text-sm text-morado font-medium">
              <span className="font-medium">Tu energía:</span> {stageSummary}
            </p>
            <p className="text-xs text-gray-600 mt-2">
              💡 <span className="font-medium">Consejo:</span> Enfócate en desarrollar esta energía durante esta etapa de tu vida.
            </p>
          </div>
        </div>

        {activeStageContent && (
          <div className="mt-5 space-y-4">
            <div className="bg-white border border-[#d4af37]/40 rounded-xl p-4 shadow-sm">
              <p className="text-xs uppercase tracking-wide text-morado font-semibold mb-2">
                Mensajes profundos
              </p>
              <p className="text-sm text-gray-700 mb-1">
                <span className="font-medium">Arquetipo:</span> {activeStageContent.archetype}
              </p>
              <p className="text-sm text-gray-700 mb-3">
                <span className="font-medium">Tema central:</span> {activeStageContent.theme}
              </p>
              {deepMessage && (
                <p className="text-sm text-gray-800 italic">
                  “{deepMessage}”
                </p>
              )}
            </div>

            {hasActions && (
              <div className="grid gap-4 md:grid-cols-2">
                <div className="bg-white border border-emerald-200 rounded-xl p-4 shadow-sm">
                  <h6 className="text-sm font-semibold text-emerald-700 flex items-center gap-1">
                    ✅ Qué sí hacer
                  </h6>
                  <ul className="mt-2 space-y-1 text-sm text-gray-700 list-disc list-inside">
                    {activeStageContent.actions?.do?.map((item, idx) => (
                      <li key={`stage-do-${activeStageNumber}-${idx}`}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div className="bg-white border border-rose-200 rounded-xl p-4 shadow-sm">
                  <h6 className="text-sm font-semibold text-rose-700 flex items-center gap-1">
                    ❌ Qué evitar
                  </h6>
                  <ul className="mt-2 space-y-1 text-sm text-gray-700 list-disc list-inside">
                    {activeStageContent.actions?.avoid?.map((item, idx) => (
                      <li key={`stage-avoid-${activeStageNumber}-${idx}`}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex justify-center">
          <a
            href="https://numerologia-cotidiana.com/mi-camino-de-vida/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-morado text-white text-sm font-semibold shadow-lg hover:opacity-90 transition uppercase tracking-wide"
          >
            SABER MÁS DE MI CAMINO DE VIDA
            <span aria-hidden="true">↗</span>
          </a>
        </div>

      </div>
    </motion.div>
  );
};

export default LifeStagesCard;
